import { ThemeProvider } from '@aws-amplify/ui-react'
import '@aws-amplify/ui-react/styles.css'
import { Amplify } from 'aws-amplify'
import type { AmplifyOutputsUnknown } from 'aws-amplify/adapter-core/internals'
import { useMemo } from 'react'
import amplifyOutputs from '../amplify_outputs.json'
import { KYCFlow } from './components/kyc-flow'

interface Theme {
  primaryColor: string
  primaryTextColor: string
  backgroundColor: string
  textColor: string
  disabledColor: string
  disabledTextColor: string
}

const defaultTheme: Theme = {
  primaryColor: '#070808',
  primaryTextColor: '#ffffff',
  backgroundColor: '#000000',
  textColor: '#fafafa',
  disabledColor: '#3f3f46',
  disabledTextColor: '#71717a',
}

function useThemeFromQuery(): Theme {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search)

    return {
      primaryColor: params.get('primaryColor') || defaultTheme.primaryColor,
      primaryTextColor:
        params.get('primaryTextColor') || defaultTheme.primaryTextColor,
      backgroundColor:
        params.get('backgroundColor') || defaultTheme.backgroundColor,
      textColor: params.get('textColor') || defaultTheme.textColor,
      disabledColor: params.get('disabledColor') || defaultTheme.disabledColor,
      disabledTextColor:
        params.get('disabledTextColor') || defaultTheme.disabledTextColor,
    }
  }, [])
}

function useAmplifyTheme() {
  const theme = useThemeFromQuery()

  return useMemo(
    (): React.ComponentProps<typeof ThemeProvider>['theme'] => ({
      name: 'notus-liveness',
      tokens: {
        colors: {
          toast: {
            primary: theme.primaryColor,
          },
          brand: {
            primary: {
              10: theme.primaryColor,
              80: theme.primaryColor,
              90: theme.primaryColor,
            },
          },
          primary: {
            10: theme.primaryColor,
            80: theme.primaryColor,
            90: theme.primaryColor,
          },
          border: {
            secondary: theme.backgroundColor,
          },
          font: {
            primary: theme.textColor,
            inverse: theme.primaryTextColor,
          },
          background: {
            primary: theme.backgroundColor,
            secondary: theme.backgroundColor,
          },
          neutral: {
            20: theme.disabledColor,
            80: theme.disabledTextColor,
          },
        },
      },
    }),
    [
      theme.backgroundColor,
      theme.disabledColor,
      theme.disabledTextColor,
      theme.primaryColor,
      theme.primaryTextColor,
      theme.textColor,
    ],
  )
}

function useThemeVars() {
  const theme = useThemeFromQuery()

  return useMemo(
    () =>
      ({
        '--kyc-primary': theme.primaryColor,
        '--kyc-primary-text': theme.primaryTextColor,
        '--kyc-bg': theme.backgroundColor,
        '--kyc-text': theme.textColor,
        '--kyc-disabled': theme.disabledColor,
        '--kyc-disabled-text': theme.disabledTextColor,
      }) as React.CSSProperties,
    [
      theme.primaryColor,
      theme.primaryTextColor,
      theme.backgroundColor,
      theme.textColor,
      theme.disabledColor,
      theme.disabledTextColor,
    ],
  )
}

if (amplifyOutputs) {
  // the worst code i've ever written
  Amplify.configure(amplifyOutputs as unknown as AmplifyOutputsUnknown)
} else {
  Amplify.configure(JSON.parse(atob(import.meta.env.VITE_AMPLIFY_OUTPUTS!)))
}

export default function App() {
  const amplifyTheme = useAmplifyTheme()
  const themeVars = useThemeVars()

  return (
    <ThemeProvider theme={amplifyTheme}>
      <div
        style={themeVars}
        className="min-h-screen bg-(--kyc-bg) text-(--kyc-text)"
      >
        <KYCFlow />
      </div>
    </ThemeProvider>
  )
}
